import { openDB } from 'idb'
import { APP_DATABASE_NAME } from '../utils/storageNames'
import type { ActiveDraft } from '../domain/note'

export interface WorkspaceScope { project: string; environment: string }
export interface WorkspaceHandle {
  readonly databaseName: string
  readonly owner: string | null
  readonly scope: WorkspaceScope
  readonly quarantinedLegacy: boolean
}

export function workspaceDatabaseName(scope: WorkspaceScope, owner: string | null): string {
  return `${APP_DATABASE_NAME}:workspace:v1:${encodeURIComponent(scope.project)}:${encodeURIComponent(scope.environment)}:${owner === null ? 'guest' : `account:${encodeURIComponent(owner)}`}`
}

/** Inspect ownership only. Never copy, erase or relabel the old collection. */
export async function chooseWorkspace(scope: WorkspaceScope, owner: string | null): Promise<WorkspaceHandle> {
  let missing = false
  const legacy = await openDB(APP_DATABASE_NAME, undefined, {
    upgrade(_db, oldVersion, _newVersion, transaction) {
      if (oldVersion === 0) { missing = true; void transaction.done.catch(() => undefined); transaction.abort() }
    },
  }).catch(error => { if (!missing) throw error; return null })
  let databaseName = workspaceDatabaseName(scope, owner)
  let quarantinedLegacy = false
  if (legacy) {
    try {
      if (legacy.objectStoreNames.contains('meta')) {
        const tx = legacy.transaction('meta', 'readwrite')
        const previousOwner = (await tx.store.get('sync-owner'))?.value
        const environment = (await tx.store.get('sync-environment'))?.value
        const project = (await tx.store.get('workspace-project:v1'))?.value
        if (owner && previousOwner === owner && environment === scope.environment && (!project || project === scope.project)) {
          databaseName = APP_DATABASE_NAME
          if (!project) await tx.store.put({ key: 'workspace-project:v1', value: scope.project })
        } else if (!previousOwner && legacy.objectStoreNames.contains('notes')) {
          quarantinedLegacy = (await legacy.count('notes')) > 0
        }
        await tx.done
      } else quarantinedLegacy = true
    } finally { legacy.close() }
  }
  return Object.freeze({ databaseName, owner, scope: Object.freeze({ ...scope }), quarantinedLegacy })
}

let enabled = false
let handle: WorkspaceHandle | null = null
let retired = false
let lease: string | null = null
const listeners = new Set<() => void | Promise<void>>()
let preservation: Promise<void> = Promise.resolve()
let draftReader: (() => ActiveDraft | null) | null = null
export function registerWorkspaceDraftReader(reader: () => ActiveDraft | null): () => void {
  draftReader = reader
  return () => { if (draftReader === reader) draftReader = null }
}
export function readRetiringDraft(): ActiveDraft | null { return draftReader?.() ?? null }
export function finishWorkspaceRetirement(): Promise<void> { return preservation }
export const WORKSPACE_TRANSITION_EVENT = 'minimany:workspace-retired'
export function enableWorkspaceBoundary(): void { enabled = true }
export function workspaceControlKey(scope: WorkspaceScope): string {
  return `minimany:workspace-control:v1:${encodeURIComponent(scope.project)}:${scope.environment}`
}
export function activeWorkspace(): WorkspaceHandle | null { return handle }
export function workspaceContentKey(key: string, databaseName = handle?.databaseName): string {
  return !databaseName || databaseName === APP_DATABASE_NAME ? key : `${databaseName}:${key}`
}
export const workspaceLockName = (databaseName: string) => `minimany:workspace-use:${databaseName}`
/** Shared lease lasts until the final outgoing draft is durable. A removal obtains
 * the exclusive counterpart, so another tab cannot be erased before it retires. */
export async function holdWorkspaceLease(): Promise<void> {
  if (!handle || !navigator.locks) return
  let acquired!: () => void
  const ready = new Promise<void>(resolve => { acquired = resolve })
  let release!: () => void
  const lifetime = new Promise<void>(resolve => { release = resolve })
  onWorkspaceRetired(() => { queueMicrotask(() => { void finishWorkspaceRetirement().then(release).catch(() => undefined) }) })
  const holding = navigator.locks.request(workspaceLockName(handle.databaseName), { mode: 'shared' }, async () => {
    acquired()
    await lifetime
  })
  await Promise.race([ready, holding])
}
/** Reconcile only a fresh document's unchanged control snapshot. Cached identity
 * chooses a namespace, never authorizes server access or adopts another copy.
 * Existing documents observe the new epoch and retire; account-entry UI still
 * requires a decision before mounting content when other copies are retained. */
export function reconcileWorkspaceIdentity(scope: WorkspaceScope, owner: string | null, expectedControl: string | null): void {
  if (handle || retired) throw new Error('A workspace is immutable for the lifetime of the document.')
  if (localStorage.getItem(workspaceControlKey(scope)) !== expectedControl) {
    throw new Error('Authentication changed while the workspace was opening. Please retry.')
  }
  publishAuthIdentity(scope, owner)
}

export function activateWorkspace(selected: WorkspaceHandle): void {
  if (handle || retired) throw new Error('A workspace is immutable for the lifetime of the document.')
  const key = workspaceControlKey(selected.scope)
  const stored = localStorage.getItem(key)
  const state = stored ? JSON.parse(stored) as { owner: string | null; signedOut?: boolean } : null
  if (state?.signedOut && selected.owner) throw new Error('Sign in again to open this offline copy.')
  if (state && state.owner !== selected.owner) throw new Error('Authentication changed while the workspace was opening. Please retry.')
  lease = state && state.owner === selected.owner ? stored : JSON.stringify({ owner: selected.owner, epoch: crypto.randomUUID() })
  localStorage.setItem(key, lease!)
  handle = selected
}
export function onWorkspaceRetired(listener: () => void | Promise<void>): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}
export function retireWorkspace(): void {
  if (retired) return
  retired = true
  if (typeof document !== 'undefined') document.documentElement.dataset.workspacePrivacy = 'locked'
  preservation = Promise.all([...listeners].map(listener => {
    try { return listener() } catch (error) { return Promise.reject(error) }
  })).then(() => undefined)
  void preservation.catch(() => undefined)
}
export function assertWorkspaceActive(): void {
  if (!enabled) return
  if (!handle || retired || localStorage.getItem(workspaceControlKey(handle.scope)) !== lease) {
    retireWorkspace()
    throw new Error('The account workspace changed. Reopen miniMany to continue.')
  }
}
export function assertWorkspaceIdentity(owner: string | null): void {
  if (!enabled || !handle) return
  if (handle.owner !== owner) retireWorkspace()
  assertWorkspaceActive()
}
export function selectedDatabaseName(): string {
  assertWorkspaceActive()
  return handle?.databaseName ?? APP_DATABASE_NAME
}
export function workspaceSignedOut(scope: WorkspaceScope): boolean {
  const value = localStorage.getItem(workspaceControlKey(scope))
  return value ? JSON.parse(value).signedOut === true : false
}
export function markWorkspaceSignedOut(): void {
  assertWorkspaceActive()
  if (!handle) throw new Error('No account workspace is open.')
  localStorage.setItem(workspaceControlKey(handle.scope), JSON.stringify({ owner: null, signedOut: true, epoch: crypto.randomUUID() }))
  retireWorkspace()
}
export function allowWorkspaceSignIn(scope: WorkspaceScope): void {
  const key = workspaceControlKey(scope)
  const raw = localStorage.getItem(key)
  if (!raw) return
  const state = JSON.parse(raw)
  // The guest workspace is unchanged when OAuth starts (including if it fails).
  // Explicit sign-in permits a genuinely new returned session after prior sign-out.
  if (!state.signedOut) return
  delete state.signedOut
  const next = JSON.stringify(state)
  localStorage.setItem(key, next)
  if (!retired && handle && key === workspaceControlKey(handle.scope)) lease = next
}

/** Called by auth persistence, before a different credential becomes available to
 * transports. Storage events propagate the durable identity boundary to other tabs. */
export function publishAuthIdentity(scope: WorkspaceScope, owner: string | null): void {
  if (!enabled) return
  const key = workspaceControlKey(scope)
  const raw = localStorage.getItem(key)
  const current = raw ? JSON.parse(raw) : null
  // A delayed refresh or sign-out from an outgoing document cannot replace or
  // erase the credentials of the new account. Only explicit local sign-out
  // cleanup may remove credentials after that document has retired.
  if (handle && (retired || raw !== lease) && !(owner === null && current?.signedOut)) {
    retireWorkspace()
    throw new Error('An outgoing workspace cannot change the current authentication.')
  }
  if (current?.signedOut && owner) throw new Error('Explicit sign-out requires a new sign-in.')
  if (current && current.owner === owner) return
  localStorage.setItem(key, JSON.stringify({ owner, epoch: crypto.randomUUID() }))
  if (handle && (handle.owner !== owner || key !== workspaceControlKey(handle.scope))) retireWorkspace()
}

// Sign-in services must stay reachable from the content-free boundary: the code of a sign-in return is redeemed while the workspace is still opening.
// Supabase Auth is recognised by its path; the Neon auth Worker is registered by its address (src/neon/runtime.ts).
const authenticationOrigins = new Set<string>()
export function allowAuthenticationOrigin(origin: string): void { authenticationOrigins.add(origin) }
export function isAuthenticationRequest(url: URL): boolean {
  return url.pathname.startsWith('/auth/v1/') || authenticationOrigins.has(url.origin)
}

/** The check runs after the SDK has supplied its token and immediately before fetch.
 * Old tasks cannot send A's payload using a newly refreshed/switched B credential. */
export function guardWorkspaceRequest(input: RequestInfo | URL, init?: RequestInit): void {
  if (!enabled) return
  const url = new URL(input instanceof Request ? input.url : String(input), globalThis.location?.href ?? 'https://localhost')
  // Authentication must remain possible from the content-free boundary.
  if (isAuthenticationRequest(url)) return
  assertWorkspaceActive()
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
  const token = headers.get('authorization')?.replace(/^Bearer\s+/i, '')
  if (!token || token.split('.').length !== 3) return
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')))
    if (payload.sub && payload.sub !== handle?.owner) throw new Error('Account changed before the request could be sent.')
  } catch { throw new Error('The request does not belong to the open account workspace.') }
}

export function resetWorkspaceForTests(): void {
  enabled = false; handle = null; retired = false; lease = null; draftReader = null; preservation = Promise.resolve(); listeners.clear(); authenticationOrigins.clear()
}
